import { stringifyUrl } from "query-string";

import { getSnapshotLoginType } from "helpers/globalContext/globalInfoSnapshot";

import { CommonConstants } from "types/constants";
import { BTLoginTypes } from "types/enum";

import { isBTAdminAuthProjectXEnabled } from "utilities/appDefaultFlags";
import { getGlobalValue } from "utilities/globalValueUtils";

export interface IIsInPortalProps {
    /** @default false */
    builder?: boolean;

    /** @default false */
    subs?: boolean;

    /** @default false */
    owner?: boolean;

    /** @default false */
    btadmin?: boolean;
}

export interface IPortalType {
    isBuilder: boolean;
    isSubs: boolean;
    isOwner: boolean;
    isBtadmin: boolean;
}

/**
 * @see BTLoginTypes
 * This matches the enum server side BTLoginPortalType - do not change names
 */
export enum PortalType {
    ALL = 0,
    BUILDER = 1,
    OWNER = 2,
    SUBS = 3,
    LEAD = 4,
    BTADMIN = 5,
}

/**
 * @returns true when the current user is on one of the passed portals
 * @example if (isInPortal({ subs: true })) { alert("hello sub!"); }
 * @example if (isInPortal({ builder: true, owner: true })) { alert("hello builder or owner!"); }
 */
export function isInPortal(portals: IIsInPortalProps): boolean {
    const currentPortalType = getCurrentPortalType();
    if (portals.owner && currentPortalType === PortalType.OWNER) {
        return true;
    }

    if (portals.subs && currentPortalType === PortalType.SUBS) {
        return true;
    }

    if (portals.builder && currentPortalType === PortalType.BUILDER) {
        return true;
    }

    if (portals.btadmin && currentPortalType === PortalType.BTADMIN) {
        return true;
    }

    return false;
}

/**
 * @deprecated Use `UserInfoContext` in components. Kept for the non-React callers that can't
 * run a hook: the PortalType request headers, `setupApix`, analytics, and `addPathForCurrentPortal`.
 */
export function getCurrentPortalType(): PortalType {
    // The condition that a path including /app/link should use the fallback for non-SPA pages will need to be removed in the future
    // This check is currently in place to address an issue where the job picker is displaying for owners when they navigate to the app via an entity link in a new tab
    // More details outlined in User Story: 173751
    const portalTypeForUrl = getPortalTypeForUrl(window.location.pathname ?? "");
    if (portalTypeForUrl !== null) {
        return portalTypeForUrl;
    }

    // Only reached off the SPA path: /app/link, webforms pages, and non-/app routes.
    //
    // The snapshot is null until the global info query resolves, and this runs synchronously
    // before that. Falling through to the global covers that window rather than reporting
    // PortalType.ALL, which the server treats as a real portal rather than "unknown".
    // eslint-disable-next-line @typescript-eslint/no-deprecated -- pre-resolution fallback; tracked by 282446
    return getPortalForLoginType(getSnapshotLoginType() ?? getGlobalValue("loginTypeInt"));
}

export function getPortalTypeForUrl(url: string): PortalType | null {
    if (url.toLowerCase().includes("/app/") && !url.toLowerCase().includes("/app/link")) {
        if (url.toLowerCase().includes("/app/subs/")) {
            return PortalType.SUBS;
        }
        if (
            url.toLowerCase().includes("/app/owner/") ||
            url.toLowerCase().includes("/owner/ownerinvoice")
        ) {
            return PortalType.OWNER;
        }
        if (url.toLowerCase().includes("/app/admin/")) {
            return PortalType.BTADMIN;
        }

        const shareToken = "[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}";
        const builderShare = new RegExp(`/app/share/${shareToken}/builder/`, "i");
        if (builderShare.test(url.toLowerCase())) {
            return PortalType.BUILDER;
        }
        const subsShare = new RegExp(`/app/share/${shareToken}/subs/`, "i");
        if (subsShare.test(url.toLowerCase())) {
            return PortalType.SUBS;
        }
        const ownerShare = new RegExp(`/app/share/${shareToken}/owner/`, "i");
        if (ownerShare.test(url.toLowerCase())) {
            return PortalType.OWNER;
        }
        const contactShare = new RegExp(`/app/share/${shareToken}/contact/`, "i");
        if (contactShare.test(url.toLowerCase())) {
            return PortalType.OWNER;
        }
        if (url.toLowerCase().includes("/app/share/")) {
            return PortalType.LEAD;
        }
        return PortalType.BUILDER;
    }
    return null;
}

/**
 * Checks the following URLs to determine if the portal should be added to the route
 * @param url The URL to check
 * @returns
 */
export function isPortalAgnosticUrl(url: string) {
    return (
        url.toLowerCase().includes("/app/link/") ||
        // Both EmailLinkClicked dispatchers: the legacy WebForms page and the APIX route it is
        // migrating to. Links in customer inboxes point at either, so both must stay matched.
        url.toLowerCase().includes("emaillinkclicked.aspx") ||
        url.toLowerCase().includes("/apix/v1/emaillinkclicked") ||
        url.toLowerCase().includes("/app/share")
    );
}

export function getPortalForLoginType(loginType: BTLoginTypes | null): PortalType {
    switch (loginType) {
        case BTLoginTypes.BUILDER:
            return PortalType.BUILDER;
        case BTLoginTypes.SUBS:
            return PortalType.SUBS;
        case BTLoginTypes.OWNER:
        case BTLoginTypes.CONTACT:
            return PortalType.OWNER;
        case BTLoginTypes.LEAD:
            return PortalType.LEAD;
        case BTLoginTypes.BTADMIN:
            return PortalType.BTADMIN;
        default:
            // should not occur in practice
            return PortalType.ALL;
    }
}

export function getLoginTypeFromPortal(portal: PortalType): BTLoginTypes {
    switch (portal) {
        case PortalType.BUILDER:
            return BTLoginTypes.BUILDER;
        case PortalType.SUBS:
            return BTLoginTypes.SUBS;
        case PortalType.OWNER:
            return BTLoginTypes.OWNER;
        case PortalType.LEAD:
            return BTLoginTypes.LEAD;
        case PortalType.BTADMIN:
            return BTLoginTypes.BTADMIN;
        default:
            // should not occur in practice
            return BTLoginTypes.ALL;
    }
}

/**
 * @summary Returns the loginType based on the current portal
 * @deprecated use UserInfoContext instead
 */
export function getLoginTypeFromCurrentPortal(): BTLoginTypes {
    return getLoginTypeFromPortal(getCurrentPortalType());
}

/**
 * Appends portalType as a query string parameter to a URL.
 * Use this for URLs that are loaded directly by the browser (e.g. img src, iframe, window.open)
 * where the PortalType header cannot be sent via apixHandler.
 * The server-side PortalContextMiddleware parses this value as BTLoginTypes.
 * @param url The URL to append the portalType to
 * @param loginType The user's login type from UserInfoContext
 * @returns The URL with the portalType query string parameter appended
 */
export function appendPortalTypeToUrl(url: string, loginType: BTLoginTypes): string {
    const separator = url.includes("?") ? "&" : "?";
    return `${url}${separator}portalType=${loginType}`;
}

export function handleRedirectToLogin() {
    const lpr = window.location.pathname + window.location.search;
    const adminLoginPageUrl = isBTAdminAuthProjectXEnabled()
        ? "/apix/v2/admin/auth/login"
        : "/api/Admin/Login";

    const loginRedirect = stringifyUrl({
        url: window.location.pathname.toLowerCase().startsWith("/app/admin")
            ? adminLoginPageUrl
            : "/",
        query: {
            lpr,
            reason: CommonConstants.Login.sessionExpired,
        },
    });

    const windowToUse = window.top ?? window;
    windowToUse.location.assign(loginRedirect);
}
